import React, { memo } from "react";

import { Box } from "@UI/Components/Box/Box";
import { Flex } from "@UI/Components/Flex/Flex";
import { Icon } from "@UI/Components/Icon/Icon";
import { Image } from "@UI/Components/Image/Image";
import { Link } from "@UI/Components/Link/Link";
import { Text } from "@UI/Components/Text/Text";
import { ChevronRight } from "@UI/Components/Icons/ChevronRight";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";

import { DraftSavedPill } from "../DraftSavedPill/DraftSavedPill";
import messages from "../../messages";

import logo from "assets/images/logos/header-logo-dark.svg";

/**
 * Exported so the screens below can subtract it from `100vh`. A bare `100vh`
 * on a box that sits *under* this header overflows the viewport by exactly this
 * much, which is the whole of RT-4340's phantom scrollbar.
 */
export const TASK_CREATION_HEADER_HEIGHT = "74px";

export interface ITaskCreationHeaderProps {
  /** Current step (1-based). Omit both `step` and `totalSteps` for the
   * description screen, which shows the logo alone. */
  step?: number;
  totalSteps?: number;
  /**
   * Makes the step indicator a button that reveals the summary. Passed on small
   * screens only: on desktop the summary is a panel beside the flow, so the
   * indicator there is plain text rather than a control that duplicates it.
   */
  onSummaryOpen?: () => void;
  /**
   * Swaps the step indicator for the "Draft saved" pill on mobile for a few
   * seconds after a save — there is no summary panel there to show it in, and
   * this is the only chrome always on screen. Desktop shows the pill in the
   * panel instead, so this is `undefined` there.
   */
  draftSavedVisible?: boolean;
  /** Bumped per save so the pill replays its entrance if a second save lands
   * while it is still up. */
  draftSavedReplayToken?: number;
}

/**
 * The flow's header (RT-4321): identical on every screen — the RingTwice logo,
 * left-aligned, plus an optional "STEP X OF Y" indicator on the right for
 * question-step screens, below `lg` only — desktop renders the counter beside
 * the progress bar instead.
 *
 * Mounted once at the base route (`ServiceRequestCreationRoot`), not per screen.
 *
 * Figma also shows a chevron next to the step text, but nothing in the file
 * says what it does. Shipped as decorative only (`aria-hidden`, no handler);
 * TODO(design): confirm whether it should open something (dropdown?
 * jump-to-step menu?) before wiring an interaction onto it.
 */
export const TaskCreationHeader = memo(
  ({
    step,
    totalSteps,
    onSummaryOpen,
    draftSavedVisible = false,
    draftSavedReplayToken,
  }: ITaskCreationHeaderProps) => {
    const showStepIndicator = Boolean(step && totalSteps);

    return (
      <Flex
        as="header"
        height={TASK_CREATION_HEADER_HEIGHT}
        paddingX={{ base: "16px", lg: "25px" }}
        background="white"
        // Figma draws this as an inset shadow rather than a border, but the
        // header is a fixed height either way, so a border cannot shift it.
        borderBottom="1px solid"
        borderColor="gray.200"
        alignItems="center"
        justifyContent="space-between"
      >
        {/* A plain anchor, not a router link: the home page is Rails-rendered,
            so it needs a real browser load. */}
        <Link href="/" display="inline-flex">
          <Image
            src={logo}
            // Names the link too, now that no `aria-label` overrides it: the
            // brand is identical in every locale, so there is nothing here a
            // translation would change.
            alt="Ring Twice"
            width="161px"
            height="31px"
          />
        </Link>
        {showStepIndicator && draftSavedVisible && (
          <Box display={{ base: "block", lg: "none" }}>
            <DraftSavedPill
              visible={draftSavedVisible}
              replayToken={draftSavedReplayToken}
            />
          </Box>
        )}
        {showStepIndicator && !draftSavedVisible && (
          // Desktop repeats the counter beside the progress bar, where it sits
          // next to the summary panel that already lists the steps — showing it
          // here as well would say the same thing twice. Hidden on the whole
          // row, chevron included.
          <Flex
            display={{ base: "flex", lg: "none" }}
            alignItems="center"
            gap="4px"
          >
            <Text
              // Plain text without a handler — the indicator is only a control
              // where it has something to reveal.
              {...(onSummaryOpen
                ? {
                    as: "button",
                    type: "button",
                    onClick: onSummaryOpen,
                    "aria-haspopup": "dialog",
                    cursor: "pointer",
                  }
                : { as: "span" })}
              fontWeight="700"
              fontSize="sm"
              lineHeight="1.5"
              color="gray.800"
              textTransform="uppercase"
            >
              <FormattedMessage
                {...messages["taskCreation.header.stepIndicator"]}
                values={{ step, total: totalSteps }}
              />
            </Text>
            <Icon
              as={ChevronRight}
              width="14px"
              height="14px"
              color="gray.800"
              aria-hidden
            />
          </Flex>
        )}
      </Flex>
    );
  }
);

TaskCreationHeader.displayName = "TaskCreationHeader";
